<script setup>
import { computed, ref, watch } from "vue";
import { useGroupStore } from "@/stores/useGroupStore";
import { useUserStore } from "@/stores/useUserStore";
import { formatDateTimeForDisplay } from "@/utils/dateUtils";
import UserCardModal from "@/components/User/UserCardModal.vue";
import EditGroupForm from "@/components/Admin/EditGroupForm.vue";
import DeleteGroupForm from "@/components/Admin/DeleteGroupForm.vue";
// ✅ agora usando a instância da API para montar a URL da foto
import api from "@/services/api";
import { useDraggableModal } from "@/composables/useDraggableModal";

const props = defineProps({
  show: { type: Boolean, required: true },
  groupId: { type: Number, required: true },
});

const emit = defineEmits(["close", "group-updated", "notify"]);

const groupStore = useGroupStore();
const userStore = useUserStore();
const isLoadingData = ref(true);

// 🔹 NOVO: arrastar o modal pelo cabeçalho. Nem todo uso desmonta o componente
// entre aberturas, então o watch de `show` recentraliza ao reabrir.
const {
  panelRef,
  panelStyle,
  isDragging,
  isDraggable,
  onHandlePointerDown,
  resetPosition,
} = useDraggableModal();

watch(
  () => props.show,
  (val) => {
    if (val) resetPosition();
  }
);

// Estados para controle do Modal de Usuário
const showUserModal = ref(false);
const selectedUserId = ref(null);

// Estado para controle do EditGroupForm
const showEditModal = ref(false);

// Estado para controle do DeleteGroupForm
const showDeleteModal = ref(false);

function normalizeUserName(name) {
  return (name ?? "").toString().trim().toLowerCase();
}

const canEdit = computed(() => {
  const logged = normalizeUserName(userStore.currentUser?.userName);
  if (!logged) return false;
  if (logged === "sys") return true;

  const creator = normalizeUserName(group.value?.createdByUserName);
  if (!creator) return true;

  return creator !== "sys";
});

function openEditModal() {
  if (!group.value?.groupId) return;
  groupStore.setSelectedGroup(group.value);
  showEditModal.value = true;
}

function handleEditRefresh() {
  groupStore.getGroupById(props.groupId);
  emit("group-updated");
}

function openDeleteModal() {
  if (!group.value?.groupId) return;
  groupStore.setSelectedGroup(group.value);
  showDeleteModal.value = true;
}

function handleDeleteClose() {
  showDeleteModal.value = false;
}

function handleDeleteRefresh() {
  showDeleteModal.value = false;
  emit("group-updated");
  emit("close");
}

function handleDeleteNotify(payload) {
  emit("notify", payload);
}

function openUserModal(userId) {
  if (!userId) return;
  selectedUserId.value = userId;
  showUserModal.value = true;
}

function closeUserModal() {
  showUserModal.value = false;
}

const group = computed(() => {
  if (
    groupStore.selectedGroup &&
    groupStore.selectedGroup.groupId === props.groupId
  ) {
    return groupStore.selectedGroup;
  }
  return groupStore.groups.find((g) => g.groupId === props.groupId) || null;
});

watch(
  () => props.groupId,
  async (newId) => {
    if (!newId) return;
    isLoadingData.value = true;
    try {
      await groupStore.getGroupById(newId);
    } catch (e) {
      console.error("Erro ao carregar grupo no CardModal:", e);
    } finally {
      isLoadingData.value = false;
    }
  },
  { immediate: true }
);

// ✅ normaliza usando baseURL da API e removendo /api
function normalizeAvatarPath(path) {
  if (!path) return "";

  // URL externa (Google, etc.)
  if (path.startsWith("http://") || path.startsWith("https://")) return path;

  // base da API sem /api no final e sem barras extras
  const base = api.defaults.baseURL.replace(/\/api$/, "").replace(/\/+$/, "");

  const cleanPath = path.startsWith("/") ? path : `/${path}`;

  return `${base}${cleanPath}`;
}

// ✅ SEM pravatar: se não tiver foto, devolve string vazia
function getCreatorAvatarUrl(data) {
  if (!data) return "";
  const path =
    data.createdByUserPhotoPath || data.createdByUser?.photoPath || "";
  if (!path) return "";
  return normalizeAvatarPath(path);
}

function getUpdaterAvatarUrl(data) {
  if (!data) return "";
  const path =
    data.updatedByUserPhotoPath || data.updatedByUser?.photoPath || "";
  if (!path) return "";
  return normalizeAvatarPath(path);
}
</script>

<template>
  <div v-if="show" class="modal-overlay" @click.self="emit('close')">
    <div ref="panelRef" class="group-card-modal-content" :style="panelStyle">
      <div
        class="modal-header"
        :class="{
 'modal-header--draggable': isDraggable,
          'modal-header--dragging': isDragging,
        }"
        @pointerdown="onHandlePointerDown"
      >
        <h3 class="card-title">Detalhes do Grupo</h3>
        <button
          @click="emit('close')"
          type="button"
          class="modal-close-btn"
          aria-label="Fechar"
        >
          <svg class="w-5 h-5" fill="currentColor" viewBox="0 0 20 20">
            <path
              fill-rule="evenodd"
              d="M4.293 4.293a1 1 0 011.414 0L10 8.586l4.293-4.293a1 1 0 111.414 1.414L11.414 10l4.293 4.293a1 1 0 01-1.414 1.414L10 11.414l-4.293 4.293a1 1 0 01-1.414-1.414L8.586 10 4.293 5.707a1 1 0 010-1.414z"
              clip-rule="evenodd"
            />
          </svg>
        </button>
      </div>

      <div v-if="group && !isLoadingData" class="card-content-grid">
        <div class="details-col">
          <div class="title-row">
            <h3 class="text-title-bold">
              {{ group.groupName }}
              <span class="title-id">(Id: {{ group.groupId }})</span>
            </h3>

            <button
              v-if="canEdit"
              type="button"
              class="btn-edit-group tooltip-bottom"
              data-tooltip="Editar grupo"
              @click.stop="openEditModal"
            >
              <i class="fas fa-pencil-alt"></i>
            </button>

            <button
              v-if="canEdit"
              type="button"
              class="btn-delete-group tooltip-bottom"
              data-tooltip="Apagar grupo"
              @click.stop="openDeleteModal"
            >
              <i class="fas fa-trash-alt"></i>
            </button>
          </div>

          <div class="meta-grid">
            <div class="meta-item meta-item-full">
              <span class="meta-label">Descrição</span>
              <span class="meta-value description-text">
                {{ group.description || "Nenhuma descrição informada." }}
              </span>
            </div>
          </div>

          <hr class="content-separator" />

          <div class="footer-info">
            <div class="users-row">
              <!-- Criador -->
              <div
                class="user-chip user-chip-clickable"
                @click="openUserModal(group.createdByUserId)"
              >
                <div class="chip-avatar">
                  <div
                    v-if="getCreatorAvatarUrl(group)"
                    class="chip-avatar-img"
                    :style="{
                      backgroundImage: `url('${getCreatorAvatarUrl(group)}')`,
                    }"
                  ></div>
                  <i
                    v-else
                    class="fas fa-user chip-avatar-icon"
                    aria-hidden="true"
                  ></i>
                </div>
                <div class="chip-text">
                  <span class="chip-label">Criador</span>
                  <span class="chip-name">
                    {{ group.createdByUserName || "Não informado" }}
                  </span>
                  <span class="chip-date">
                    {{ formatDateTimeForDisplay(group.createdAt) }}
                  </span>
                </div>
              </div>

              <!-- Atualizador -->
              <div
                class="user-chip user-chip-clickable"
                @click="openUserModal(group.updatedByUserId)"
              >
                <div class="chip-avatar">
                  <div
                    v-if="getUpdaterAvatarUrl(group)"
                    class="chip-avatar-img"
                    :style="{
                      backgroundImage: `url('${getUpdaterAvatarUrl(group)}')`,
                    }"
                  ></div>
                  <i
                    v-else
                    class="fas fa-user chip-avatar-icon"
                    aria-hidden="true"
                  ></i>
                </div>
                <div class="chip-text">
                  <span class="chip-label">Atualizador</span>
                  <span class="chip-name">
                    {{ group.updatedByUserName || "Não informado" }}
                  </span>
                  <span class="chip-date">
                    {{ formatDateTimeForDisplay(group.updatedAt) }}
                  </span>
                </div>
              </div>
            </div>
          </div>
        </div>
      </div>

      <div v-else class="loading-state">
        <i class="fas fa-spinner fa-spin"></i> Carregando detalhes do grupo...
      </div>

    </div>
  </div>

  <!-- Sub-modais via Teleport.
       Todos são position: fixed e o painel deste card ganha transform ao ser
       arrastado — um ancestral com transform viraria o containing block deles,
       fazendo o overlay cobrir só o painel e acompanhar o arrasto. Fora do
       painel no DOM, seguem ancorados na viewport. -->
  <Teleport to="body">
    <UserCardModal
      v-if="showUserModal"
      :show="showUserModal"
      :userId="selectedUserId"
      @close="closeUserModal"
    />

    <EditGroupForm
      v-if="showEditModal"
      :groupId="props.groupId"
      @close="showEditModal = false"
      @refresh="handleEditRefresh"
    />

    <DeleteGroupForm
      v-if="showDeleteModal && group"
      :group="group"
      @close="handleDeleteClose"
      @refresh="handleDeleteRefresh"
      @notify="handleDeleteNotify"
    />
  </Teleport>
</template>

<style scoped>
.modal-overlay {
  width: 100%;
  height: 100%;
}

.group-card-modal-content {
  background: var(--surface);
  border-radius: 12px;
  width: 90%;
  max-width: 650px;
  position: relative;
  box-shadow: var(--shadow-modal);
  margin-bottom: 50px;
  overflow: hidden;
}

/* Os botões do cabeçalho continuam sendo clique, não alça */
.modal-header--draggable .modal-close-btn,
.modal-header--draggable .modal-header-actions button {
  cursor: pointer;
}

.card-title {
  margin: 0;
  font-size: 1.05rem;
  font-weight: 600;
  color: var(--text-dark);
}

.card-content-grid {
  padding: 1rem;
}

.details-col {
  width: 100%;
}

.title-row {
  display: flex;
  align-items: center;
  justify-content: flex-start;
  gap: 6px;
  margin-bottom: 12px;
}

.btn-edit-group {
  display: inline-flex;
  align-items: center;
  justify-content: center;
  flex-shrink: 0;
  width: 18px;
  height: 18px;
  padding: 0;
  border-radius: 5px;
  border: 1px solid var(--info-border);
  background-color: var(--info-bg);
  color: var(--info-text);
  cursor: pointer;
  transition: all 0.15s ease;
  position: relative;
}
.btn-edit-group i {
  font-size: 0.6rem;
}
.btn-edit-group:hover {
  background-color: var(--info-border);
  transform: translateY(-1px);
}

.btn-delete-group {
  display: inline-flex;
  align-items: center;
  justify-content: center;
  flex-shrink: 0;
  width: 18px;
  height: 18px;
  padding: 0;
  border-radius: 5px;
  border: 1px solid var(--danger-bg);
  background-color: var(--danger-bg);
  color: var(--danger-text);
  cursor: pointer;
  transition: all 0.15s ease;
  position: relative;
}
.btn-delete-group i {
  font-size: 0.6rem;
}
.btn-delete-group:hover {
  background-color: var(--danger-bg);
  transform: translateY(-1px);
}

.tooltip-bottom {
  position: relative;
}
.tooltip-bottom::after {
  content: attr(data-tooltip);
  position: absolute;
  top: calc(100% + 6px);
  left: 50%;
  transform: translateX(-50%) translateY(-2px);
  background: var(--tooltip-bg);
  color: var(--tooltip-text);
  font-size: 0.68rem;
  font-weight: 500;
  line-height: 1;
  padding: 5px 8px;
  border-radius: 4px;
  white-space: nowrap;
  opacity: 0;
  transition: opacity 0.15s ease, transform 0.15s ease;
  pointer-events: none;
  z-index: 80;
}
.tooltip-bottom:hover::after {
  opacity: 1;
  transform: translateX(-50%) translateY(0);
  transition-delay: 0.8s;
}

.text-title-bold {
  font-size: 1.05rem;
  font-weight: 700;
  margin: 0;
  color: var(--text-dark);
  line-height: 1.25;
}

.title-id {
  font-size: 0.75rem;
  font-weight: 600;
  color: var(--text-muted);
  text-transform: uppercase;
  letter-spacing: 0.04em;
  margin-left: 4px;
}

.footer-info {
  padding-top: 8px;
  width: 100%;
}

.users-row {
  display: flex;
  gap: 12px;
  flex-direction: row;
  flex-wrap: wrap;
  margin-top: 8px;
}

</style>
